import { missingChannelEpisodeFilters } from "@/components/dataset/episodeReviewState";
import { DatasetEpisodeWarningBadge } from "@/components/dataset/DatasetEpisodeWarningBadge";
import { episodeReviewSavedStore } from "@/components/dataset/episodeReviewSavedStore";
import {
  ContentSwitcher,
  DataTableSource,
  IconButton,
  createDataTableColumnVisibility,
  type DataTableColumn,
  type DataTableColumnVisibility,
} from "@carbon6/c6-frontend-design";
import { useCallback, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  addBookmark,
  type DatasetFolderNode,
  type DatasetFolderTree,
  type DatasetMergeJobResp,
  type EpisodeListFilters,
} from "@/api";
import { DATASET_STORAGE_BACKENDS, type DatasetStorageBackend } from "@/api/datasetStorageBackend";
import type { UserRole } from "@/app";
import {
  addDatasetUuid,
  applyUrlPageToSource,
  goToNextPage,
  goToPreviousPage,
  includesDatasetUuid,
  mirrorSourcePageToUrl,
  removeDatasetUuid,
} from "@/utils";
import { usePageParam } from "@/app/usePageParam";
import {
  DatasetCard,
  DatasetPageContent,
  DatasetUploadButton,
  DatasetViewModeToggle,
  createDatasetCardModel,
  createThumbnailRefreshDebouncer,
  datasetUploadAction,
  formatEmptyDatasetMessage,
  resolveErrorMessage,
  type DatasetViewMode,
} from "@/components/dataset";
import { GnbMenu } from "@/components/base";
import styles from "./DatasetPage.module.css";
import type { LoadState } from "@/components/dataset/loadState";
import {
  storageBackendFromParam,
  storageBackendSearchParamKey,
  writeStorageBackendParam,
} from "@/components/dataset/storageBackendGate";
import {
  storageBackendLabel,
  trainingUnsupportedReason,
} from "@/components/dataset/storageBackendText";
import {
  DatasetFolderDialogs,
  DatasetFolderLane,
  folderSearchParamKey,
  folderSelectionFromParam,
  folderToken,
  useDatasetFolderTree,
  writeFolderParam,
  type FolderDialogRequest,
  type FolderSelection,
} from "@/components/dataset/folder";
import { isConflictError } from "@/http";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import { openDatasetDetailWindow } from "@/app/datasetDetailWindow";
import { BookmarksTableSource } from "./BookmarksTableSource";
import type { DatasetCriteriaTableSource } from "./DatasetCriteriaTableSource";
import {
  DatasetCardRowActions,
  type DatasetCardTrainAction,
} from "./components/DatasetCardRowActions";
import { openTrainingSubmitWindowForDataset } from "./datasetTrainEntry";
import { DatasetMergeJobChip } from "./components/DatasetMergeJobChip";
import { DatasetSelectionActions } from "./components/DatasetSelectionActions";
import { buildDatasetColumns } from "./datasetColumns";
import {
  DatasetFilterBlock,
  DatasetFilterController,
  type DatasetFilterDefaultValues,
} from "@/components/filter";
import type { DatasetTableRow } from "./DatasetTableRow";
import { DatasetTableSource } from "./DatasetTableSource";
import type { DatasetPageViewState } from "./DatasetPageTypes";
import {
  archivedTabValue,
  bookmarkTabValue,
  datasetTabItems,
  datasetTabSearchParamKey,
  defaultDatasetTab,
  normalizeDatasetTab,
  uploadsTabValue,
  type DatasetTab,
} from "./datasetTabs";
import { DatasetUploadStatusPanel } from "@/page/datasetUpload/DatasetUploadStatusPanel";
import { TableEmptyState } from "@/components/table/TableEmptyState";
import type { EmptyStateAction } from "@/shared/ui/emptyState/public";

const defaultPageSize = 20;
// 저장 위치 전환기의 선택지. `전체` 는 게이트 없음이고 URL 에 키를 남기지 않는다.
const allStorageBackendsValue = "all";
const storageBackendOptions = [
  { value: allStorageBackendsValue, label: "전체" },
  ...DATASET_STORAGE_BACKENDS.map((backend) => ({
    value: backend,
    label: storageBackendLabel(backend),
  })),
];
// 서비스 앱이 소유하는 Datasets 필터 초기화 기준. 공용 컨트롤러는 이 값을 보존하고 복원만 한다.
const datasetLibraryFilterDefaults: DatasetFilterDefaultValues = {
  searchQuery: "",
  selection: {},
  labelTokens: [],
  matchMode: "all",
};

export function DatasetPage() {
  const { page, setPage } = usePageParam();
  const [searchParams, setSearchParams] = useSearchParams();
  // 폴더는 다른 조회 축(컨트롤러+localStorage)과 달리 URL 이 소유한다. `?tab=`·`?page=` 와
  // 같은 곳에 둔다. 그래야 정리해 둔 위치를 링크로 그대로 전달할 수 있다.
  const folderSelection = folderSelectionFromParam(searchParams.get(folderSearchParamKey));
  // 저장 위치도 URL 이 소유한다. 폴더와 달리 세 목록(Dataset·북마크·보관됨)에 함께 적용한다.
  const storageBackend = storageBackendFromParam(searchParams.get(storageBackendSearchParamKey));
  // 폴더 트리의 건수도 같은 저장 위치로 받는다. 그래야 폴더 옆 건수가 그 폴더로 거른 목록의 total 과 같다.
  const {
    state: folderTreeState,
    countsCurrent: folderCountsCurrent,
    reload: reloadFolderTree,
  } = useDatasetFolderTree(storageBackend);
  const currentTab = normalizeDatasetTab(searchParams.get(datasetTabSearchParamKey));

  // 탭은 URL ?tab= 로 보존한다(기본 datasets 는 키 생략). 전환 시 단일 ?page= 를 1로 초기화한다.
  const handleTabChange = useCallback(
    (value: string) => {
      const tab = normalizeDatasetTab(value);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === defaultDatasetTab) {
            next.delete(datasetTabSearchParamKey);
          } else {
            next.set(datasetTabSearchParamKey, tab);
          }
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleFolderSelect = useCallback(
    (next: FolderSelection) => {
      setSearchParams((prev) => writeFolderParam(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const handleStorageBackendChange = useCallback(
    (value: string | null) => {
      setSearchParams((prev) => writeStorageBackendParam(prev, storageBackendFromParam(value)), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  // 제목 옆 저장 위치 전환기(docs/rules.md §17). 저장 위치는 필터로 좁히기 전에 "어느 묶음을 보는지"를
  // 정한다. 그래서 필터 줄이 아니라 제목 옆에 두고, 필터 초기화가 이 값을 지우지 않는다. 업로드 현황
  // 탭은 Dataset 목록이 아니라서 표시하지 않는다.
  const storageBackendSwitcher =
    currentTab === uploadsTabValue ? undefined : (
      <ContentSwitcher
        label="저장 위치"
        value={storageBackend ?? allStorageBackendsValue}
        options={storageBackendOptions}
        onValueChange={handleStorageBackendChange}
      />
    );

  return (
    <DatasetPageView
      pageTitle="Datasets"
      currentMenu={GnbMenu.Dataset}
      // belowTabs 필터 블록이 이미 헤더와 본문의 경계라 tabpanel 상단 여백을 겹쳐 두지 않는다.
      // 업로드 탭은 필터 블록이 없으므로(패널이 자기 필터를 소유한다) 표준 상단 여백을 다시 쓴다.
      // 그러면 탭 줄과 필터 바 사이 간격이 데이터셋 탭의 탭↔검색 영역 간격과 같아진다.
      bodyFlushTop={currentTab !== uploadsTabValue}
      page={page}
      onPageChange={setPage}
      folderTreeState={folderTreeState}
      folderCountsCurrent={folderCountsCurrent}
      folderSelection={folderSelection}
      onFolderSelect={handleFolderSelect}
      onFoldersChanged={reloadFolderTree}
      storageBackend={storageBackend}
      titleAside={storageBackendSwitcher}
      tabs={{
        items: datasetTabItems,
        value: currentTab,
        defaultValue: defaultDatasetTab,
        ariaLabel: "Dataset 섹션",
        onValueChange: handleTabChange,
      }}
    />
  );
}

type DatasetPageViewProps = PageBaseProps & {
  page: number;
  onPageChange: (page: number) => void;
  folderTreeState: LoadState<DatasetFolderTree>;
  // 트리의 건수가 지금 저장 위치의 것인지다. 저장 위치를 바꾼 직후 새 트리를 받기 전에는 false 다.
  folderCountsCurrent: boolean;
  folderSelection: FolderSelection;
  onFolderSelect: (selection: FolderSelection) => void;
  // 폴더가 바뀌었다(트리 편집, 데이터셋 이동). 건수가 바뀌므로 트리를 다시 받는다.
  onFoldersChanged: () => void;
  // 제목 옆 전환기에서 고른 저장 위치. null 은 전체다.
  storageBackend: DatasetStorageBackend | null;
};

class DatasetPageView extends PageBase<DatasetPageViewProps, DatasetPageViewState> {
  // 목록을 바꾸는 액션(보관, 복구, 담기/빼기, 상세 창 닫힘) 후에는 활성 소스만 재조회한다.
  // 비활성 소스는 탭이 활성화될 때 어차피 다시 읽는다(activateSource 의 "활성화마다 재조회" 정책).
  private readonly refreshActiveList = (): void => {
    void this.getActiveSource().refresh();
    this.syncFacetCounts();
  };

  // 필터 드롭다운의 선택지별 개수. facets 모집단(available + 폴더 + 저장 위치)이 Dataset 탭 목록과
  // 같을 때만 받는다. 보관됨·북마크 탭은 모집단이 달라 그 개수가 틀리므로 배지를 제거한다. 폴더·저장
  // 위치 변경, 탭 전환, 목록을 바꾼 작업 뒤에 호출한다. 개수는 다른 필터 조건과 독립이라 조건 변경에는 호출하지 않는다.
  private syncFacetCounts(): void {
    if (this.getActiveTab() === defaultDatasetTab) {
      void this.datasetFilterController.loadFacetCounts({
        folder: folderToken(this.props.folderSelection),
        storageBackend: this.props.storageBackend,
      });
    } else {
      this.datasetFilterController.clearFacetCounts();
    }
  }

  // 세 탭 공용 행 클릭. 상세 창에서 archive/복구/담기가 바뀌었을 수 있으므로 닫을 때 현재
  // 활성 목록을 갱신한다. 창이 열린 동안 탭이 바뀌었어도 보이는 목록이 갱신 대상이다.
  private readonly handleRowClick = (row: DatasetTableRow): void => {
    openDatasetDetailWindow({
      datasetUuid: row.uuid,
      displayName: row.display_name,
      onClose: this.refreshActiveList,
    });
  };

  // 경고 버튼이 그 에피소드를 연다. 상세 창의 에피소드 탭을 필터 하나로 연다. 창을 닫으면 목록을
  // 다시 읽는다. 경고 종류는 필터만 다르다.
  private openEpisodeReview(row: DatasetTableRow, episodeFilters: EpisodeListFilters): void {
    openDatasetDetailWindow({
      datasetUuid: row.uuid,
      displayName: row.display_name,
      initialTab: "episodeReview",
      episodeFilters,
      onClose: this.refreshActiveList,
    });
  }

  private readonly handlePendingCandidatesClick = (row: DatasetTableRow): void => {
    this.openEpisodeReview(row, { quality_candidate: "pending" });
  };

  // 채널 누락 에피소드는 업로드 때부터 제외 상태다. 사유 필터가 걸리면 에피소드 목록이 제외된 행도
  // 보여 준다. 그래서 건수와 행 수가 같다.
  private readonly handleMissingChannelClick = (row: DatasetTableRow): void => {
    this.openEpisodeReview(row, missingChannelEpisodeFilters());
  };

  // 카드 위쪽의 경고 버튼들. 수가 0 인 경고는 빼고, 남은 것이 없으면 슬롯을 비운다.
  private readonly renderCardWarnings = (row: DatasetTableRow): ReactNode => {
    const pending = row.quality_candidate_pending_count ?? 0;
    const missing = row.missing_channel_episode_count;
    if (pending === 0 && missing === 0) {
      return null;
    }
    return (
      <>
        <DatasetEpisodeWarningBadge
          kind="qualityCandidate"
          count={row.quality_candidate_pending_count}
          onOpen={() => this.handlePendingCandidatesClick(row)}
        />
        <DatasetEpisodeWarningBadge
          kind="missingChannel"
          count={missing}
          onOpen={() => this.handleMissingChannelClick(row)}
        />
      </>
    );
  };

  private readonly renderRowActions = (dataset: DatasetTableRow): ReactNode =>
    this.renderDatasetRowActions(this.datasetSource, dataset);

  private readonly renderArchivedRowActions = (dataset: DatasetTableRow): ReactNode =>
    this.renderDatasetRowActions(this.archivedSource, dataset);

  // 공용 Dataset 행 액션. 학습(텍스트)과 북마크에 담기(별)다. Edit/Archive(비활성화·복구)는 상세에서만 한다.
  // 가용·보관됨 목록이 같은 액션을 쓰고, 로딩 판정만 각자 소스에서 읽는다.
  private renderDatasetRowActions(source: DatasetTableSource, dataset: DatasetTableRow): ReactNode {
    const isLoading = source.getSnapshot().loadState === "loading";
    const isAdded = includesDatasetUuid(this.state.bookmarkUuids, dataset.uuid);
    const isAdding = includesDatasetUuid(this.state.addingDatasetUuids, dataset.uuid);

    return (
      <DatasetCardRowActions
        train={this.buildTrainRowAction(dataset)}
        bookmark={{
          isAdded,
          disabled: isLoading || isAdded || isAdding,
          tooltip: isAdded
            ? "북마크에 담겨 있습니다."
            : isAdding
              ? "북마크에 담고 있습니다."
              : "북마크에 담습니다.",
          onPress: () => this.handleAddBookmarkPress(dataset),
        }}
      />
    );
  }

  // 학습 진입은 available 데이터셋만 가능하다. 보관됨 목록 행은 status 게이트로 자동 제외된다.
  // 저장 위치가 학습을 지원하지 않으면 버튼을 숨기지 않고 끈다. 사유를 툴팁으로 알린다.
  private buildTrainRowAction(dataset: DatasetTableRow): DatasetCardTrainAction | undefined {
    if (dataset.status !== "available") {
      return undefined;
    }
    const disabledReason = trainingUnsupportedReason(dataset);
    return {
      onPress: () =>
        openTrainingSubmitWindowForDataset({
          uuid: dataset.uuid,
          displayName: dataset.display_name,
        }),
      ...(disabledReason === null ? {} : { disabledReason }),
    };
  }

  // 북마크 탭 행 액션. 다른 목록과 같은 학습(텍스트)·별 인라인 액션이다. 이 탭의 행은 모두 담긴
  // 것이라 별이 채워져 있다. 별을 누르면 북마크에서 뺀다(공용 Dataset 자체는 그대로). 학습 게이트는
  // buildTrainRowAction 과 같다. 담은 뒤 보관된 항목이나 읽기 전용 등급이면 학습 버튼이 빠진다.
  private readonly renderBookmarkRowActions = (row: DatasetTableRow): ReactNode => {
    const isLoading = this.bookmarkSource.getSnapshot().loadState === "loading";
    const isRemoving = includesDatasetUuid(this.state.removingDatasetUuids, row.uuid);

    return (
      <DatasetCardRowActions
        train={this.buildTrainRowAction(row)}
        bookmark={{
          isAdded: true,
          disabled: isLoading || isRemoving,
          tooltip: isLoading
            ? "북마크 목록을 불러오고 있습니다."
            : isRemoving
              ? "북마크에서 빼고 있습니다."
              : "북마크에서 뺍니다.",
          onPress: () => void this.handleRemoveBookmark(row),
        }}
      />
    );
  };

  private readonly datasetSource = new DatasetTableSource({
    pageSize: defaultPageSize,
    rowActionsBuilder: this.renderRowActions,
    rowClickHandler: this.handleRowClick,
    resolveErrorMessage,
    statusFilter: "available",
  });

  // 보관된(archived) dataset 목록. 가용 목록과 status 축만 다르다. 비활성 탭 소스라
  // 첫 활성화 때 lazy 로드한다(페이지 진입마다 미리 받지 않는다).
  private readonly archivedSource = new DatasetTableSource({
    pageSize: defaultPageSize,
    rowActionsBuilder: this.renderArchivedRowActions,
    rowClickHandler: this.handleRowClick,
    resolveErrorMessage,
    statusFilter: "archived",
  });

  // 내가 담아 둔 공용 Dataset(GET /v1/me/bookmarks). 카드/컬럼은 Dataset 목록과 공유한다.
  private readonly bookmarkSource = new BookmarksTableSource({
    pageSize: defaultPageSize,
    rowActionsBuilder: this.renderBookmarkRowActions,
    rowClickHandler: this.handleRowClick,
    resolveErrorMessage,
  });

  // 세 탭의 목록 소스. 세 목록에 함께 적용하는 축(저장 위치)이 순회한다.
  private get listSources(): readonly (DatasetTableSource | BookmarksTableSource)[] {
    return [this.datasetSource, this.archivedSource, this.bookmarkSource];
  }

  // 카드 대표 섬네일 presigned URL 이 만료되거나 실패하면 목록을 재조회해 새 URL 을 받는다.
  private readonly handleThumbnailExpired = createThumbnailRefreshDebouncer(() => {
    void this.datasetSource.refresh();
  });

  private readonly handleArchivedThumbnailExpired = createThumbnailRefreshDebouncer(() => {
    void this.archivedSource.refresh();
  });

  private readonly handleBookmarkThumbnailExpired = createThumbnailRefreshDebouncer(() => {
    void this.bookmarkSource.refresh();
  });

  // 테이블 컬럼은 탭별 섬네일 만료 핸들러를 주입해 한 번만 만든다. 렌더링마다 다시 만들지 않는다(컬럼 메뉴·가시성 식별을 안정적으로 유지한다).
  // 메모 작성·삭제 후 목록 갱신은 DatasetResourceView 의 ListRefresh 컨텍스트가 처리한다.
  private readonly datasetTableColumns = buildDatasetColumns({
    onPendingCandidatesClick: this.handlePendingCandidatesClick,
    onMissingChannelClick: this.handleMissingChannelClick,
    onThumbnailError: this.handleThumbnailExpired,
  });
  private readonly archivedTableColumns = buildDatasetColumns({
    onPendingCandidatesClick: this.handlePendingCandidatesClick,
    onMissingChannelClick: this.handleMissingChannelClick,
    onThumbnailError: this.handleArchivedThumbnailExpired,
  });
  private readonly bookmarkTableColumns = buildDatasetColumns({
    onPendingCandidatesClick: this.handlePendingCandidatesClick,
    onMissingChannelClick: this.handleMissingChannelClick,
    onThumbnailError: this.handleBookmarkThumbnailExpired,
  });

  // 공유 필터 컨트롤러. 검색, facet, match-mode, 라벨 카탈로그 로딩을 한 곳에서 소유한다.
  // UI 는 belowTabs 슬롯(renderBelowTabs)에 한 번만 두고, 커밋은 활성 탭 소스로 전달한다.
  // 세 탭이 조건을 공유하고, 탭을 전환하면 activateSource 가 새 활성 소스의 조건을 맞춘다.
  private readonly datasetFilterController = new DatasetFilterController({
    defaultValues: datasetLibraryFilterDefaults,
    onSearchQueryChange: (query) => {
      void this.getActiveSource().setSearchQuery(query);
    },
    onFilterSelectionChange: (selection, mode) => {
      void this.getActiveSource().setFilterSelection(selection, mode);
    },
    resolveErrorMessage,
  });

  state: DatasetPageViewState = {
    viewMode: "grid",
    tableColumnVisibility: createDataTableColumnVisibility(this.datasetTableColumns),
    bookmarkUuids: [],
    addingDatasetUuids: [],
    removingDatasetUuids: [],
    actionErrorMessage: null,
    mergeJob: null,
    folderDialog: null,
  };

  private disposed = false;
  private unsubscribeEpisodeReview: (() => void) | undefined;

  // 최신 렌더의 actor role. renderRowActions(테이블이 행마다 호출)는 context 를 받지 못한다.
  // 그래서 renderContent 에서 저장해 행 액션의 권한 게이트에 쓴다.
  private currentUserRole: UserRole | null = null;

  protected override async initPage(): Promise<void> {
    this.disposed = false;
    this.unsubscribeEpisodeReview = episodeReviewSavedStore.subscribe(this.refreshActiveList);
    this.datasetFilterController.init();
    this.datasetSource.subscribe(this.handleSourceChange);
    this.archivedSource.subscribe(this.handleSourceChange);
    this.bookmarkSource.subscribe(this.handleSourceChange);
    this.datasetFilterController.subscribe(() => {
      if (!this.disposed) {
        this.forceUpdate();
      }
    });
    // 딥링크 `?folder=` 를 첫 요청에 넣는다. 여기서 setFolderToken 을 쓰면 아래 goToPage 와
    // 겹쳐 첫 페이지를 두 번 받는다.
    this.datasetSource.seedFolderToken(folderToken(this.props.folderSelection));
    // 딥링크 `?storage_backend=` 도 같다. 세 목록에 함께 적용하므로 세 소스에 모두 넣는다.
    for (const source of this.listSources) {
      source.seedStorageBackend(this.props.storageBackend);
    }
    // URL 의 page(딥링크, 뒤로가기 복귀)로 활성 탭 소스만 로드한다. 비활성 소스는 탭이 처음
    // 활성화될 때 lazy 로드한다(activateSource). 활성화마다 재조회하는 정책이라 미리 로드하면 낭비다.
    // 업로드 현황으로 진입했으면 데이터셋 소스는 로드하지 않고 카탈로그만 준비한다.
    this.syncFacetCounts();
    await Promise.all([
      this.isUploadsTab() ? Promise.resolve() : this.getActiveSource().goToPage(this.props.page),
      this.datasetFilterController.loadCatalog(),
    ]);
  }

  protected override disposePage(): void {
    this.disposed = true;
    this.unsubscribeEpisodeReview?.();
    this.datasetFilterController.dispose();
    this.datasetSource.dispose();
    this.archivedSource.dispose();
    this.bookmarkSource.dispose();
  }

  // URL page/탭이 외부에서 바뀌면(prev/next, 탭 전환, 뒤로/앞으로) 활성 source 를 맞춘다.
  componentDidUpdate(prevProps: DatasetPageViewProps): void {
    const prevTab = normalizeDatasetTab(prevProps.tabs?.value);
    const nextTab = this.getActiveTab();
    const tabChanged = prevTab !== nextTab;
    // 폴더 축은 데이터셋 탭만 쓴다. 트리 건수가 available 범위라 보관됨 탭과는 비교가
    // 성립하지 않는다. 북마크 탭은 지금 범위 밖이다.
    const nextFolder = folderToken(this.props.folderSelection);
    const folderChanged = folderToken(prevProps.folderSelection) !== nextFolder;
    // 저장 위치는 세 목록에 함께 적용한다. 보이는 목록만 다시 받고 나머지는 값만 바꾼다. 나머지는
    // 탭이 활성화될 때 그 값으로 다시 받는다(activateSource). 탭이 함께 바뀌었으면(뒤로/앞으로)
    // 모두 값만 바꾸고, 아래의 탭 분기가 새 활성 목록을 다시 받는다.
    if (prevProps.storageBackend !== this.props.storageBackend) {
      // 같은 갱신에서 폴더도 바뀔 수 있다. 예: 사이드바의 Datasets 링크는 두 키를 함께 지운다. 폴더는
      // 값만 먼저 맞춘다. 아래의 재조회 한 번이 두 값을 함께 싣는다.
      this.datasetSource.seedFolderToken(nextFolder);
      const visible = tabChanged || this.isUploadsTab() ? null : this.getActiveSource();
      for (const source of this.listSources) {
        if (source === visible) {
          void source.setStorageBackend(this.props.storageBackend);
        } else {
          source.seedStorageBackend(this.props.storageBackend);
        }
      }
      if (!tabChanged) {
        this.syncFacetCounts();
        return;
      }
    } else if (folderChanged) {
      void this.datasetSource.setFolderToken(nextFolder);
      this.syncFacetCounts();
      return;
    }
    if (tabChanged) {
      this.activateSource();
      this.syncFacetCounts();
      return;
    }
    if (prevProps.page !== this.props.page && !this.isUploadsTab()) {
      applyUrlPageToSource(this.getActiveSource(), this.props.page);
    }
  }

  // 탭 활성화. 새 활성 소스가 공유 필터의 최종 조건을 따라잡는다. 조건이 같아도 한 번 재조회해
  // 다른 탭에서 만든 변경(북마크 담기, 보관/복구)을 반영한다. 재조회는 page 캐시를 쓰지 않는다.
  // 캐시에는 탭을 떠나기 전의 page 가 있다. 분기가 서로 배타라 어느 경로든 활성화마다 fetch 는 1회다.
  private activateSource(): void {
    if (this.isUploadsTab()) {
      return;
    }
    const source = this.getActiveSource();
    const { query, selection, matchMode } = this.datasetFilterController.currentCriteria();
    if (source.applyCriteria(query, selection, matchMode)) {
      return; // 조건 따라잡기가 이미 page 1 재조회를 만들었다.
    }
    // 첫 활성화의 lazy 로드와 재활성화가 같은 경로다. URL 의 page(딥링크 포함)를 받는다.
    void source.refresh(this.props.page);
  }

  // 제목 오른쪽의 데이터셋 업로드 버튼. 새 데이터셋 추가는 탭(가용, 북마크, 보관됨)과 관계없는
  // 페이지 단위 진입점이라 헤더에 고정한다.
  protected override renderHeaderActions(): ReactNode {
    return <DatasetUploadButton onClosed={this.handleUploadWindowClosed} />;
  }

  // 탭 줄 아래(탭과 본문 사이). 검색, 필터 레일, 고급 필터 진입을 탭 공통 조건 블록으로
  // 고정한다. header 안, tabpanel 밖이라 탭을 전환해도 마운트가 유지되고, 세 탭이 같은
  // 조건(공유 컨트롤러)을 본다. 결과 수, 뷰 토글, 새로고침은 레일이 아니라 목록 상단
  // BulkActionBar 스트립이 소유한다(DatasetPageContent).
  protected override renderBelowTabs(context: PageBaseContext): ReactNode {
    // 업로드 현황은 필터 축이 다르다(상태 그룹, 업로드 범위). 그래서 데이터셋 조건 레일을 두지 않고
    // 패널이 tabpanel 안에서 자기 필터를 소유한다.
    if (this.isUploadsTab()) {
      return null;
    }
    return (
      <DatasetFilterBlock
        controller={this.datasetFilterController}
        bookmarkSurface="datasetLibrary"
        bookmarkUid={context.currentUserUid}
      />
    );
  }

  protected override renderContent(context: PageBaseContext): ReactNode {
    this.currentUserRole = context.currentUserRole;
    const activeTab = this.getActiveTab();
    if (activeTab === bookmarkTabValue) {
      return this.renderBookmarksTab();
    }
    if (activeTab === uploadsTabValue) {
      return (
        <DatasetUploadStatusPanel
          currentUserRole={context.currentUserRole}
          currentUserMemberUuid={context.currentUserMemberUuid}
        />
      );
    }
    if (activeTab === archivedTabValue) {
      return this.renderArchivedTab();
    }
    return this.renderDatasetsTab();
  }

  // 데이터셋·북마크 탭의 공통 셸. 표시 조립(툴바, 카드/테이블 뷰, 페이지네이션, 컬럼 메뉴)은
  // DatasetPageContent 가 소유한다. 여기서는 탭별 정책(소스, 컬럼, 문구, 새로고침·섬네일 핸들러)과
  // 페이지 공통 상태(뷰 모드, 컬럼 가시성, 액션 에러, URL 페이지 콜백)만 연결한다. 검색·필터는
  // 탭 밖(belowTabs)의 renderBelowTabs 가 소유한다.
  private renderResourceTab(config: {
    source: DataTableSource<DatasetTableRow>;
    columns: readonly DataTableColumn<DatasetTableRow>[];
    layoutAriaLabel: string;
    viewAriaLabel: string;
    summaryLabel: string;
    emptyMessage: string;
    // 빈 결과 안내 옆 동작. 조건 미매칭처럼 안내 문장만 필요하면 두지 않는다.
    emptyAction?: EmptyStateAction;
    loadingMessage: string;
    onThumbError: () => void;
    notice?: ReactNode;
    // 선택 체크박스와 선택 액션은 데이터셋 탭에만 있다(북마크 탭과 보관됨 목록에는 없다).
    showCheckbox?: boolean;
    selectionActions?: ReactNode;
    footerLeading?: ReactNode;
  }): ReactNode {
    const { tableColumnVisibility, viewMode, actionErrorMessage } = this.state;
    const isLoading = config.source.getSnapshot().loadState === "loading";

    return (
      <DatasetPageContent
        source={config.source}
        columns={config.columns}
        renderCard={(row) => (
          <DatasetCard
            item={createDatasetCardModel(row)}
            warningActions={this.renderCardWarnings(row)}
            imageUrl={row.thumbnail_url}
            onImageError={config.onThumbError}
            actions={row.hasRowActions ? row.renderRowActions() : null}
            memoDatasetUuid={row.uuid}
            recentMemo={row.latest_memo}
            memoCount={row.memo_count}
          />
        )}
        viewMode={viewMode}
        summaryLabel={config.summaryLabel}
        layoutAriaLabel={config.layoutAriaLabel}
        viewAriaLabel={config.viewAriaLabel}
        emptyMessage={
          <TableEmptyState action={config.emptyAction}>{config.emptyMessage}</TableEmptyState>
        }
        loadingMessage={config.loadingMessage}
        minWidth="1080px"
        actionsColumnWidth="116px"
        columnVisibility={tableColumnVisibility}
        onColumnVisibilityChange={this.handleTableColumnVisibilityChange}
        showCheckbox={config.showCheckbox}
        selectionActions={config.selectionActions}
        // 바 오른쪽 끝 [새로고침][그리드/테이블 스위처]. 선택 중에도 자리를 유지하는 탭 공통
        // 도구다. 뷰 모드는 페이지 상태라 탭을 오가도 유지된다. 로딩 판정은 탭 자신의
        // 소스를 본다. 바 높이 계약(sm 컨트롤)에 맞춰 둘 다 sm 으로 넣는다.
        barRightContent={
          <>
            <IconButton
              size="sm"
              text={isLoading ? "목록을 불러오고 있습니다." : "새로고침"}
              variant="surface"
              disabled={isLoading}
              onClick={this.handleRefreshClick}
            />
            <DatasetViewModeToggle
              size="sm"
              value={viewMode}
              onChange={this.handleViewModeChange}
            />
          </>
        }
        footerLeading={config.footerLeading}
        notice={config.notice}
        error={actionErrorMessage}
        onPreviousPage={this.handlePreviousPageClick}
        onNextPage={this.handleNextPageClick}
      />
    );
  }

  // 트리를 성공적으로 받았을 때만 값이 있다. 이동 대상 목록과 다이얼로그가 이 값을 쓴다.
  private get folderTree(): DatasetFolderTree | null {
    const state = this.props.folderTreeState;
    return state.status === "success" ? state.data : null;
  }

  private readonly openFolderDialog = (request: FolderDialogRequest): void => {
    this.setState({ folderDialog: request });
  };

  // 삭제는 저장 위치와 무관하게 그 폴더의 모든 Dataset 을 미분류로 보낸다. 트리의 건수가 모든 저장
  // 위치의 합일 때만 그 수로 영향을 알린다. 저장 위치로 좁힌 건수는 그 영향보다 작을 수 있다.
  private readonly openFolderDeleteDialog = (node: DatasetFolderNode): void => {
    const countsAllStorageBackends =
      this.props.storageBackend === null && this.props.folderCountsCurrent;
    this.openFolderDialog({
      kind: "delete",
      node,
      datasetCount: countsAllStorageBackends ? node.dataset_count : null,
    });
  };

  private readonly closeFolderDialog = (): void => {
    this.setState({ folderDialog: null });
  };

  // 폴더 쓰기가 끝났다. 트리 건수와 목록의 `폴더` 컬럼이 함께 바뀐다.
  private readonly handleFoldersChanged = (): void => {
    this.props.onFoldersChanged();
    this.refreshActiveList();
  };

  /**
   * 빈 목록의 안내를 고른다. 순서는 검색·필터 조건 → 저장 위치 → 탭의 기본 문구다.
   *
   * 저장 위치만 고른 채로 비면 그 위치에 없다고 알린다. "아직 없다"고 말하면 다른 저장 위치의
   * Dataset 을 가린 것이 보이지 않는다.
   *
   * @param subject 그 탭의 대상. 예) `"보관된 Dataset이"`
   * @param plain 좁힌 것이 없을 때의 문구.
   * @returns `narrowed` 는 조건이나 저장 위치로 좁힌 목록인지다. 좁혔으면 업로드를 권하지 않는다.
   */
  private resolveEmptyMessage(
    subject: string,
    plain: string,
  ): { message: string; narrowed: boolean } {
    const filterSnapshot = this.datasetFilterController.getSnapshot();
    if (filterSnapshot.search.hasQuery || filterSnapshot.filter.hasSelection) {
      return {
        message: formatEmptyDatasetMessage({
          hasSearchQuery: filterSnapshot.search.hasQuery,
          searchQuery: filterSnapshot.search.query,
          hasFilterSelection: filterSnapshot.filter.hasSelection,
        }),
        narrowed: true,
      };
    }
    if (this.props.storageBackend !== null) {
      const location = storageBackendLabel(this.props.storageBackend);
      return {
        message: `${location} 저장 위치에는 ${subject} 없습니다. 제목 옆에서 저장 위치를 바꿔 보세요.`,
        narrowed: true,
      };
    }
    return { message: plain, narrowed: false };
  }

  private renderDatasetsTab(): ReactNode {
    const { message: emptyMessage, narrowed } = this.resolveEmptyMessage(
      "Dataset이",
      formatEmptyDatasetMessage({
        hasSearchQuery: false,
        searchQuery: "",
        hasFilterSelection: false,
      }),
    );

    const content = this.renderResourceTab({
      source: this.datasetSource,
      columns: this.datasetTableColumns,
      layoutAriaLabel: "Dataset 목록",
      viewAriaLabel: "Dataset",
      summaryLabel: "Dataset",
      emptyMessage,
      // 데이터셋이 하나도 없을 때만 업로드 창을 바로 연다. 조건이나 저장 위치로 좁힌 목록이 비면 다음 할
      // 일은 그 조건을 바꾸는 것이다.
      emptyAction: narrowed ? undefined : datasetUploadAction(this.handleUploadWindowClosed),
      loadingMessage: "Dataset을 불러오고 있습니다.",
      onThumbError: this.handleThumbnailExpired,
      showCheckbox: true,
      // 선택 자체는 source 가 소유해 페이지를 넘겨도 유지된다. 액션은 그 스냅샷만 읽는다.
      selectionActions: (
        <DatasetSelectionActions
          selected={this.datasetSource.getSelectedRows()}
          currentUserRole={this.currentUserRole}
          mergeActive={this.state.mergeJob !== null}
          onMergeStarted={this.handleMergeStarted}
          onCompleted={this.handleSelectionActionCompleted}
          folderTree={this.folderTree}
          onFoldersChanged={this.props.onFoldersChanged}
        />
      ),
      // 통합 진행 칩. 선택을 지운 뒤에도 잡이 실행되는 동안 계속 보여야 하므로 바가 아니라
      // footer 왼쪽에 둔다.
      footerLeading:
        this.state.mergeJob !== null ? (
          <DatasetMergeJobChip job={this.state.mergeJob} onFinished={this.handleMergeJobFinished} />
        ) : null,
    });

    // 필터 블록(belowTabs)은 전체 폭 그대로 두고, 그 아래 본문만 좌측 레인과 나눈다.
    // 폴더는 "어느 묶음을 보고 있나" 이고 필터는 그 안에서 좁히는 축이라 자리가 다르다.
    return (
      <div className={styles.folderLaneLayout}>
        <DatasetFolderLane
          state={this.props.folderTreeState}
          selection={this.props.folderSelection}
          onSelect={this.props.onFolderSelect}
          currentUserRole={this.currentUserRole}
          onCreate={(parentUuid) => this.openFolderDialog({ kind: "create", parentUuid })}
          onRename={(node) => this.openFolderDialog({ kind: "rename", node })}
          onMove={(node) => this.openFolderDialog({ kind: "move", node })}
          onDelete={this.openFolderDeleteDialog}
          onRetry={this.props.onFoldersChanged}
          countsHidden={!this.props.folderCountsCurrent}
        />
        <div className={styles.folderLaneBody}>{content}</div>
        {this.folderTree === null ? null : (
          <DatasetFolderDialogs
            request={this.state.folderDialog}
            tree={this.folderTree}
            currentUserRole={this.currentUserRole}
            onClose={this.closeFolderDialog}
            onChanged={this.handleFoldersChanged}
          />
        )}
      </div>
    );
  }

  // 보관됨 탭. 읽기 전용 열람이라 선택 액션을 두지 않는다. 통합은 서버가 422 로 거부하고,
  // 보관은 이미 보관된 상태라 성립하지 않는다. 눌리는 버튼을 두고 실패로 알리지 않는다.
  // 복구(Restore)는 상세 창에서만 한다. 검색·필터 조건은 가용 목록과 공유한다(같은 컨트롤러).
  private renderArchivedTab(): ReactNode {
    const { message: emptyMessage } = this.resolveEmptyMessage(
      "보관된 Dataset이",
      "보관된 Dataset이 없습니다.",
    );

    return this.renderResourceTab({
      source: this.archivedSource,
      columns: this.archivedTableColumns,
      layoutAriaLabel: "보관된 Dataset 목록",
      viewAriaLabel: "보관된 Dataset",
      summaryLabel: "Dataset",
      emptyMessage,
      loadingMessage: "보관된 Dataset을 불러오고 있습니다.",
      onThumbError: this.handleArchivedThumbnailExpired,
    });
  }

  // 북마크 탭. 검색·필터 조건은 다른 탭과 공유하고(같은 컨트롤러), 서버가 담긴 범위 안에서
  // 좁힌다. 조건 때문에 결과가 비면 "없음" 이 아니라 조건 미매칭 안내를 표시한다.
  private renderBookmarksTab(): ReactNode {
    const { message: emptyMessage } = this.resolveEmptyMessage(
      "담아 둔 Dataset이",
      "아직 담아 둔 Dataset이 없습니다. Dataset 목록에서 별을 눌러 담아 보세요.",
    );

    return this.renderResourceTab({
      source: this.bookmarkSource,
      columns: this.bookmarkTableColumns,
      layoutAriaLabel: "북마크 목록",
      viewAriaLabel: "북마크",
      summaryLabel: "Dataset",
      emptyMessage,
      loadingMessage: "북마크를 불러오고 있습니다.",
      onThumbError: this.handleBookmarkThumbnailExpired,
    });
  }

  private getActiveTab(): DatasetTab {
    return normalizeDatasetTab(this.props.tabs?.value);
  }

  // 업로드 현황 탭은 데이터셋 소스와 공유 필터를 쓰지 않는다. 패널이 자기 소스·필터·`?page=` 를
  // 모두 소유한다. 그래서 이 탭이 활성인 동안 페이지는 소스를 다루지 않는다.
  private isUploadsTab(): boolean {
    return this.getActiveTab() === uploadsTabValue;
  }

  // 활성 탭 소스. criteria 커밋 대상이고, URL page·재조회의 단일 대상이다.
  private getActiveSource(): DatasetCriteriaTableSource {
    const activeTab = this.getActiveTab();
    if (activeTab === bookmarkTabValue) {
      return this.bookmarkSource;
    }
    return activeTab === archivedTabValue ? this.archivedSource : this.datasetSource;
  }

  // 새로고침(목록 스트립 오른쪽). 활성 탭 목록과 라벨 카탈로그(필터 facet vocab)를 함께
  // 재조회한다. 탭 공통 도구라 어느 탭에서든 뜻이 같다.
  private readonly handleRefreshClick = () => {
    this.setState({ actionErrorMessage: null });
    this.refreshActiveList();
    void this.datasetFilterController.loadCatalog();
  };

  // 업로드 창이 닫히면(완료/폐기) 목록과 라벨 카탈로그를 다시 읽는다. 새 데이터셋이 가진
  // 라벨이 아직 필터 facet 에 없을 수 있다.
  private readonly handleUploadWindowClosed = () => {
    void this.datasetSource.refresh();
    void this.datasetFilterController.loadCatalog();
    this.syncFacetCounts();
  };

  // 선택 액션이 끝나면 선택을 비우고, 목록이 바뀐 액션(통합·보관)만 재조회한다.
  private readonly handleSelectionActionCompleted = ({ listChanged }: { listChanged: boolean }) => {
    this.datasetSource.clearSelection();
    if (listChanged) {
      this.refreshActiveList();
    }
  };

  // 통합 큐잉(202). 잡을 상태에 저장해 footer 진행 칩을 표시하고 통합 버튼을 잠근다.
  private readonly handleMergeStarted = (mergeJob: DatasetMergeJobResp) => {
    this.setState({ mergeJob });
  };

  // 통합 잡 관측 종료(성공, 실패, 폴링 중단). 칩을 제거하고 선택 액션 공통 정리를 따른다.
  private readonly handleMergeJobFinished = ({ listChanged }: { listChanged: boolean }) => {
    this.setState({ mergeJob: null });
    this.handleSelectionActionCompleted({ listChanged });
  };

  private readonly handleViewModeChange = (viewMode: DatasetViewMode) => {
    this.setState({ viewMode });
  };

  private readonly handleTableColumnVisibilityChange = (
    tableColumnVisibility: DataTableColumnVisibility,
  ) => {
    this.setState({ tableColumnVisibility });
  };

  private readonly handlePreviousPageClick = () => {
    goToPreviousPage(this.getActiveSource(), this.props.onPageChange);
  };

  private readonly handleNextPageClick = () => {
    goToNextPage(this.getActiveSource(), this.props.onPageChange);
  };

  private readonly handleAddBookmarkPress = (dataset: DatasetTableRow) => {
    void this.submitAddBookmark(dataset.uuid);
  };

  // 별 누름(북마크 탭) → 북마크에서 뺀다. 진행 중에는 별을 잠가 같은 행의 DELETE 가 겹치지 않게
  // 한다. 성공하면 bookmarkUuids 에서도 제거해 Datasets 탭의 별이 비고 다시 담을 수 있게 한다.
  private readonly handleRemoveBookmark = async (row: DatasetTableRow): Promise<void> => {
    if (includesDatasetUuid(this.state.removingDatasetUuids, row.uuid)) {
      return;
    }
    this.setState((state) => ({
      removingDatasetUuids: addDatasetUuid(state.removingDatasetUuids, row.uuid),
      actionErrorMessage: null,
    }));
    try {
      await this.bookmarkSource.removeBookmark(row);
      if (this.disposed) {
        return;
      }
      this.setState((state) => ({
        bookmarkUuids: removeDatasetUuid(state.bookmarkUuids, row.uuid),
        removingDatasetUuids: removeDatasetUuid(state.removingDatasetUuids, row.uuid),
      }));
    } catch (error) {
      if (this.disposed) {
        return;
      }
      this.setState((state) => ({
        removingDatasetUuids: removeDatasetUuid(state.removingDatasetUuids, row.uuid),
        actionErrorMessage: resolveErrorMessage(error),
      }));
    }
  };

  private readonly handleSourceChange = () => {
    if (this.disposed) {
      return;
    }
    // 검색·필터 변경으로 활성 source 가 page 를 1로 되돌리면 URL 에도 반영한다.
    mirrorSourcePageToUrl(this.getActiveSource(), this.props.page, this.props.onPageChange);
    this.forceUpdate();
  };

  // 별 누름 → 북마크에 담는다. 409(이미 담김)는 성공과 같게 처리해 별을 채운다.
  private async submitAddBookmark(datasetUuid: string): Promise<void> {
    if (
      includesDatasetUuid(this.state.bookmarkUuids, datasetUuid) ||
      includesDatasetUuid(this.state.addingDatasetUuids, datasetUuid)
    ) {
      return;
    }

    this.setState((state) => ({
      addingDatasetUuids: addDatasetUuid(state.addingDatasetUuids, datasetUuid),
      actionErrorMessage: null,
    }));

    try {
      await addBookmark(datasetUuid);
      if (this.disposed) {
        return;
      }
      this.markAdded(datasetUuid);
    } catch (error) {
      if (this.disposed) {
        return;
      }
      if (isConflictError(error)) {
        this.markAdded(datasetUuid);
        return;
      }
      this.setState((state) => ({
        addingDatasetUuids: removeDatasetUuid(state.addingDatasetUuids, datasetUuid),
        actionErrorMessage: resolveErrorMessage(error),
      }));
    }
  }

  private markAdded(datasetUuid: string): void {
    this.setState((state) => ({
      bookmarkUuids: addDatasetUuid(state.bookmarkUuids, datasetUuid),
      addingDatasetUuids: removeDatasetUuid(state.addingDatasetUuids, datasetUuid),
      actionErrorMessage: null,
    }));
  }
}
